/**
 * Procedural Animation Builder - Create animations programmatically for rigged models
 * 
 * This allows you to define keyframe animations in code for models that have
 * a skeleton but no pre-baked animations.
 */

import { Animation } from "@babylonjs/core/Animations/animation";
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { Bone } from "@babylonjs/core/Bones/bone";
import { Scene } from "@babylonjs/core/scene";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";

/**
 * Keyframe for bone animation
 */
export interface BoneKeyframe {
    frame: number;
    rotation?: { x: number; y: number; z: number };  // Euler angles in degrees
    rotationQuaternion?: Quaternion;
    position?: Vector3;
    scale?: Vector3;
}

/**
 * Track definition for a single bone
 */
export interface BoneTrack {
    boneName: string;
    keyframes: BoneKeyframe[];
}

/**
 * Complete animation definition
 */
export interface AnimationDefinition {
    name: string;
    frameRate: number;
    totalFrames: number;
    loop: boolean;
    tracks: BoneTrack[];
}

/**
 * Common animation presets
 */
export type AnimationPreset = 
    | "idle" 
    | "breathe" 
    | "walk" 
    | "run" 
    | "wave" 
    | "look_around"
    | "jump"
    | "attack_swipe";

/**
 * ProceduralAnimationBuilder - Create animations from code
 */
export class ProceduralAnimationBuilder {
    private _scene: Scene;
    private _skeleton: Skeleton;
    private _boneMap: Map<string, Bone> = new Map();
    
    constructor(skeleton: Skeleton, _mesh: TransformNode, scene: Scene) {
        this._skeleton = skeleton;
        // mesh parameter available for bone world-space calculations if needed
        this._scene = scene;
        
        // Build bone lookup map (case-insensitive)
        skeleton.bones.forEach(bone => {
            this._boneMap.set(bone.name.toLowerCase(), bone);
        });
        
        console.log(`ProceduralAnimationBuilder: Initialized with ${skeleton.bones.length} bones`);
        console.log(`  Bones: ${skeleton.bones.map(b => b.name).join(", ")}`);
    }
    
    /**
     * Find a bone by name (case-insensitive, partial match)
     */
    public findBone(name: string): Bone | null {
        const lowerName = name.toLowerCase();
        
        // Exact match first
        if (this._boneMap.has(lowerName)) {
            return this._boneMap.get(lowerName)!;
        }
        
        // Partial match
        for (const [boneName, bone] of this._boneMap) {
            if (boneName.includes(lowerName) || lowerName.includes(boneName)) {
                return bone;
            }
        }
        
        return null;
    }
    
    /**
     * Get all bone names
     */
    public getBoneNames(): string[] {
        return this._skeleton.bones.map(b => b.name);
    }
    
    /**
     * Build an animation group from a definition
     */
    public buildAnimation(definition: AnimationDefinition): AnimationGroup {
        const group = new AnimationGroup(definition.name, this._scene);
        
        for (const track of definition.tracks) {
            const bone = this.findBone(track.boneName);
            if (!bone) {
                console.warn(`ProceduralAnimationBuilder: Bone "${track.boneName}" not found, skipping track`);
                continue;
            }
            
            // Create rotation animation if any keyframe has rotation
            if (track.keyframes.some(kf => kf.rotation || kf.rotationQuaternion)) {
                const rotAnim = this._createRotationAnimation(
                    `${definition.name}_${track.boneName}_rotation`,
                    track.keyframes,
                    definition.frameRate
                );
                group.addTargetedAnimation(rotAnim, bone);
            }
            
            // Create position animation if any keyframe has position
            if (track.keyframes.some(kf => kf.position)) {
                const posAnim = this._createPositionAnimation(
                    `${definition.name}_${track.boneName}_position`,
                    track.keyframes,
                    definition.frameRate
                );
                group.addTargetedAnimation(posAnim, bone);
            }
            
            // Create scale animation if any keyframe has scale
            if (track.keyframes.some(kf => kf.scale)) {
                const scaleAnim = this._createScaleAnimation(
                    `${definition.name}_${track.boneName}_scale`,
                    track.keyframes,
                    definition.frameRate
                );
                group.addTargetedAnimation(scaleAnim, bone);
            }
        }
        
        // Set animation range
        group.normalize(0, definition.totalFrames);
        
        console.log(`ProceduralAnimationBuilder: Created animation "${definition.name}" with ${definition.tracks.length} tracks`);
        
        return group;
    }
    
    /**
     * Create rotation animation from keyframes
     */
    private _createRotationAnimation(
        name: string,
        keyframes: BoneKeyframe[],
        frameRate: number
    ): Animation {
        const anim = new Animation(
            name,
            "rotationQuaternion",
            frameRate,
            Animation.ANIMATIONTYPE_QUATERNION,
            Animation.ANIMATIONLOOPMODE_CYCLE
        );
        
        const keys: { frame: number; value: Quaternion }[] = [];
        
        for (const kf of keyframes) {
            if (kf.rotationQuaternion) {
                keys.push({ frame: kf.frame, value: kf.rotationQuaternion.clone() });
            } else if (kf.rotation) {
                // Convert euler (degrees) to quaternion
                const rad = Math.PI / 180;
                const q = Quaternion.FromEulerAngles(
                    kf.rotation.x * rad,
                    kf.rotation.y * rad,
                    kf.rotation.z * rad
                );
                keys.push({ frame: kf.frame, value: q });
            }
        }
        
        anim.setKeys(keys);
        return anim;
    }
    
    /**
     * Create position animation from keyframes
     */
    private _createPositionAnimation(
        name: string,
        keyframes: BoneKeyframe[],
        frameRate: number
    ): Animation {
        const anim = new Animation(
            name,
            "position",
            frameRate,
            Animation.ANIMATIONTYPE_VECTOR3,
            Animation.ANIMATIONLOOPMODE_CYCLE
        );
        
        const keys: { frame: number; value: Vector3 }[] = [];
        
        for (const kf of keyframes) {
            if (kf.position) {
                keys.push({ frame: kf.frame, value: kf.position.clone() });
            }
        }
        
        anim.setKeys(keys);
        return anim;
    }
    
    /**
     * Create scale animation from keyframes
     */
    private _createScaleAnimation(
        name: string,
        keyframes: BoneKeyframe[],
        frameRate: number
    ): Animation {
        const anim = new Animation(
            name,
            "scaling",
            frameRate,
            Animation.ANIMATIONTYPE_VECTOR3,
            Animation.ANIMATIONLOOPMODE_CYCLE
        );
        
        const keys: { frame: number; value: Vector3 }[] = [];
        
        for (const kf of keyframes) {
            if (kf.scale) {
                keys.push({ frame: kf.frame, value: kf.scale.clone() });
            }
        }
        
        anim.setKeys(keys);
        return anim;
    }
    
    /**
     * Create a simple breathing/idle animation
     * Gently moves the spine/chest for a breathing effect
     */
    public createBreathingAnimation(options?: {
        spineBone?: string;
        chestBone?: string;
        intensity?: number;
        speed?: number;
    }): AnimationGroup {
        const intensity = options?.intensity ?? 2; // degrees
        const frameRate = 30;
        const cycleFrames = Math.round(120 / (options?.speed ?? 1)); // ~4 seconds at speed 1
        
        const tracks: BoneTrack[] = [];
        
        // Try to find spine bone
        const spineNames = [options?.spineBone, "spine", "spine1", "spine.001", "torso"];
        for (const name of spineNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: cycleFrames / 2, rotation: { x: intensity * 0.5, y: 0, z: 0 } },
                        { frame: cycleFrames, rotation: { x: 0, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        // Try to find chest bone
        const chestNames = [options?.chestBone, "chest", "spine2", "spine.002", "upperchest"];
        for (const name of chestNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: cycleFrames / 2, rotation: { x: intensity, y: 0, z: 0 } },
                        { frame: cycleFrames, rotation: { x: 0, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        return this.buildAnimation({
            name: "idle",
            frameRate,
            totalFrames: cycleFrames,
            loop: true,
            tracks
        });
    }
    
    /**
     * Create a simple walk cycle animation
     * Alternates legs and arms with torso sway
     */
    public createWalkAnimation(options?: {
        hipBone?: string;
        leftLeg?: string;
        rightLeg?: string;
        leftArm?: string;
        rightArm?: string;
        stepAngle?: number;
        armSwing?: number;
        speed?: number;
    }): AnimationGroup {
        const stepAngle = options?.stepAngle ?? 30; // degrees
        const armSwing = options?.armSwing ?? 20; // degrees
        const frameRate = 30;
        const cycleFrames = Math.round(30 / (options?.speed ?? 1)); // ~1 second cycle
        
        const tracks: BoneTrack[] = [];
        
        // Hip/pelvis sway
        const hipNames = [options?.hipBone, "hip", "hips", "pelvis", "root"];
        for (const name of hipNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: 0, y: 0, z: -2 } },
                        { frame: cycleFrames / 4, rotation: { x: 0, y: 0, z: 2 } },
                        { frame: cycleFrames / 2, rotation: { x: 0, y: 0, z: -2 } },
                        { frame: cycleFrames * 3/4, rotation: { x: 0, y: 0, z: 2 } },
                        { frame: cycleFrames, rotation: { x: 0, y: 0, z: -2 } },
                    ]
                });
                break;
            }
        }
        
        // Left leg
        const leftLegNames = [options?.leftLeg, "thigh.l", "upperleg.l", "leftupperleg", "leg.l", "l_thigh"];
        for (const name of leftLegNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: stepAngle, y: 0, z: 0 } },
                        { frame: cycleFrames / 2, rotation: { x: -stepAngle, y: 0, z: 0 } },
                        { frame: cycleFrames, rotation: { x: stepAngle, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        // Right leg (opposite phase)
        const rightLegNames = [options?.rightLeg, "thigh.r", "upperleg.r", "rightupperleg", "leg.r", "r_thigh"];
        for (const name of rightLegNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: -stepAngle, y: 0, z: 0 } },
                        { frame: cycleFrames / 2, rotation: { x: stepAngle, y: 0, z: 0 } },
                        { frame: cycleFrames, rotation: { x: -stepAngle, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        // Left arm (opposite to left leg)
        const leftArmNames = [options?.leftArm, "upperarm.l", "arm.l", "leftupperarm", "l_upperarm"];
        for (const name of leftArmNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: -armSwing, y: 0, z: 0 } },
                        { frame: cycleFrames / 2, rotation: { x: armSwing, y: 0, z: 0 } },
                        { frame: cycleFrames, rotation: { x: -armSwing, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        // Right arm (opposite to right leg)
        const rightArmNames = [options?.rightArm, "upperarm.r", "arm.r", "rightupperarm", "r_upperarm"];
        for (const name of rightArmNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: armSwing, y: 0, z: 0 } },
                        { frame: cycleFrames / 2, rotation: { x: -armSwing, y: 0, z: 0 } },
                        { frame: cycleFrames, rotation: { x: armSwing, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        return this.buildAnimation({
            name: "walk",
            frameRate,
            totalFrames: cycleFrames,
            loop: true,
            tracks
        });
    }
    
    /**
     * Create a run animation (faster walk with more exaggerated movement)
     */
    public createRunAnimation(options?: {
        hipBone?: string;
        leftLeg?: string;
        rightLeg?: string;
        leftArm?: string;
        rightArm?: string;
        stepAngle?: number;
        armSwing?: number;
    }): AnimationGroup {
        // Run is basically a faster walk with bigger movements
        const group = this.createWalkAnimation({
            ...options,
            stepAngle: (options?.stepAngle ?? 30) * 1.5,
            armSwing: (options?.armSwing ?? 20) * 1.5,
            speed: 2, // Twice as fast
        });
        
        // Rename it
        group.name = "run";
        
        return group;
    }
    
    /**
     * Create a look-around idle animation
     */
    public createLookAroundAnimation(options?: {
        headBone?: string;
        neckBone?: string;
        turnAngle?: number;
    }): AnimationGroup {
        const turnAngle = options?.turnAngle ?? 30;
        const frameRate = 30;
        const cycleFrames = 180; // 6 seconds
        
        const tracks: BoneTrack[] = [];
        
        // Head movement
        const headNames = [options?.headBone, "head", "skull"];
        for (const name of headNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: 30, rotation: { x: 5, y: turnAngle, z: 0 } },
                        { frame: 60, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: 90, rotation: { x: -5, y: -turnAngle, z: 0 } },
                        { frame: 120, rotation: { x: 10, y: 0, z: 0 } },
                        { frame: 150, rotation: { x: -10, y: turnAngle * 0.5, z: 0 } },
                        { frame: 180, rotation: { x: 0, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        // Neck follows head slightly
        const neckNames = [options?.neckBone, "neck"];
        for (const name of neckNames) {
            if (name && this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: 30, rotation: { x: 2, y: turnAngle * 0.3, z: 0 } },
                        { frame: 60, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: 90, rotation: { x: -2, y: -turnAngle * 0.3, z: 0 } },
                        { frame: 120, rotation: { x: 5, y: 0, z: 0 } },
                        { frame: 150, rotation: { x: -5, y: turnAngle * 0.2, z: 0 } },
                        { frame: 180, rotation: { x: 0, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        return this.buildAnimation({
            name: "look_around",
            frameRate,
            totalFrames: cycleFrames,
            loop: true,
            tracks
        });
    }
    
    /**
     * Create a simple attack/swipe animation
     */
    public createAttackAnimation(options?: {
        armBone?: string;
        shoulderBone?: string;
        hand?: "left" | "right";
        swipeAngle?: number;
    }): AnimationGroup {
        const swipeAngle = options?.swipeAngle ?? 90;
        const hand = options?.hand ?? "right";
        const frameRate = 30;
        const totalFrames = 30; // 1 second
        
        const tracks: BoneTrack[] = [];
        
        // Arm swing
        const armSuffix = hand === "left" ? [".l", "_l", "left", "l_"] : [".r", "_r", "right", "r_"];
        const armNames = options?.armBone 
            ? [options.armBone]
            : armSuffix.flatMap(s => [`upperarm${s}`, `arm${s}`, `${s}upperarm`]);
            
        for (const name of armNames) {
            if (this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: 5, rotation: { x: -30, y: 30, z: 0 } }, // Wind up
                        { frame: 12, rotation: { x: 60, y: -swipeAngle, z: 0 } }, // Swipe
                        { frame: 20, rotation: { x: 30, y: -swipeAngle * 0.5, z: 0 } }, // Follow through
                        { frame: 30, rotation: { x: 0, y: 0, z: 0 } }, // Return
                    ]
                });
                break;
            }
        }
        
        // Shoulder follows
        const shoulderNames = options?.shoulderBone
            ? [options.shoulderBone]
            : armSuffix.flatMap(s => [`shoulder${s}`, `clavicle${s}`]);
            
        for (const name of shoulderNames) {
            if (this.findBone(name)) {
                tracks.push({
                    boneName: name,
                    keyframes: [
                        { frame: 0, rotation: { x: 0, y: 0, z: 0 } },
                        { frame: 5, rotation: { x: 0, y: 10, z: 0 } },
                        { frame: 12, rotation: { x: 0, y: -15, z: 0 } },
                        { frame: 20, rotation: { x: 0, y: -10, z: 0 } },
                        { frame: 30, rotation: { x: 0, y: 0, z: 0 } },
                    ]
                });
                break;
            }
        }
        
        return this.buildAnimation({
            name: "attack",
            frameRate,
            totalFrames,
            loop: false,
            tracks
        });
    }
    
    /**
     * Create preset animations and return them as an array
     */
    public createPresets(presets: AnimationPreset[]): AnimationGroup[] {
        const groups: AnimationGroup[] = [];
        
        for (const preset of presets) {
            let group: AnimationGroup | null = null;
            
            switch (preset) {
                case "idle":
                case "breathe":
                    group = this.createBreathingAnimation();
                    if (preset === "breathe") group.name = "breathe";
                    break;
                case "walk":
                    group = this.createWalkAnimation();
                    break;
                case "run":
                    group = this.createRunAnimation();
                    break;
                case "look_around":
                    group = this.createLookAroundAnimation();
                    break;
                case "attack_swipe":
                    group = this.createAttackAnimation();
                    break;
                case "wave":
                    // TODO: Implement wave animation
                    console.warn(`Preset "${preset}" not yet implemented`);
                    break;
                case "jump":
                    // TODO: Implement jump animation
                    console.warn(`Preset "${preset}" not yet implemented`);
                    break;
            }
            
            if (group) {
                groups.push(group);
            }
        }
        
        return groups;
    }
    
    /**
     * Dispose of created animations
     */
    public dispose(): void {
        this._boneMap.clear();
    }
}
